/**
 * Kakara Theme - Product JavaScript
 */

(function() {
  'use strict';

  const product = {
    init() {
      this.container = document.querySelector('[data-product-template]');
      if (!this.container) return;

      this.productJson = this.getProductJson();
      if (!this.productJson) return;

      this.currentVariant = this.resolveInitialVariant();
      this.bindEvents();
      this.updateSpecs(this.currentVariant);
      this.updatePackDetail(this.currentVariant);
      this.updateSizeFit(this.currentVariant);
      this.updateScentStory(this.currentVariant);
      this.initStickyAtc();
      this.loadRecommendations();
    },

    resolveInitialVariant() {
      const params = new URLSearchParams(window.location.search);
      const urlId = params.get('variant');
      if (urlId) {
        const fromUrl = this.productJson.variants.find((v) => String(v.id) === String(urlId));
        if (fromUrl) return fromUrl;
      }

      const selectedId = this.container.querySelector('[data-variant-id]')?.value;
      if (selectedId) {
        const fromLiquid = this.productJson.variants.find((v) => String(v.id) === String(selectedId));
        if (fromLiquid) return fromLiquid;
      }

      return this.productJson.variants.find((v) => v.available) || this.productJson.variants[0];
    },

    getProductJson() {
      const jsonElement = this.container.querySelector('[data-product-json]');
      if (jsonElement) {
        try {
          return JSON.parse(jsonElement.textContent);
        } catch (e) {
          console.error('Error parsing product JSON:', e);
        }
      }
      return null;
    },

    bindEvents() {
      // Thumbnail clicks
      this.container.querySelectorAll('[data-product-thumbnail]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.applyThumbnailImage(thumb);
        });
      });

      // Option buttons
      this.container.querySelectorAll('[data-option-buttons]').forEach((group) => {
        group.querySelectorAll('[data-option-value]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.updateOption(btn.dataset.optionValue, group);
          });
        });
      });

      // Option selects
      this.container.querySelectorAll('[data-option-select]').forEach((select) => {
        select.addEventListener('change', () => {
          this.syncOptionButtons(select.dataset.optionIndex, select.value);
          this.updateVariantFromOptions();
        });
      });

      // Quantity lives outside the form visually but submits via the HTML form= attribute.

      // Candle + matching $9.99 hanger (one ATC for both)
      const roadBtn = this.container.querySelector('[data-candle-road-atc]');
      if (roadBtn) {
        roadBtn.addEventListener('click', () => this.addCandleWithHanger(roadBtn));
      }
    },

    async addCandleWithHanger(btn) {
      if (!btn || btn.disabled || btn.classList.contains('is-success')) return;

      const addonId = parseInt(btn.dataset.addonVariantId, 10);
      const candleId = this.currentVariant?.id;
      if (!addonId || !candleId || !this.currentVariant?.available) return;

      const qtyInput = this.container.querySelector('[data-quantity-input]');
      const quantity = Math.max(1, parseInt(qtyInput?.value, 10) || 1);

      const originalText = btn.textContent;
      btn.disabled = true;
      btn.classList.add('is-success');
      btn.textContent = 'Added!';
      const startedAt = performance.now();

      try {
        const response = await fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              { id: candleId, quantity },
              { id: addonId, quantity }
            ]
          })
        });
        if (!response.ok) throw new Error('Failed to add candle and hanger');
        await response.json();

        if (window.KakaraCart?.refreshUI) {
          await window.KakaraCart.refreshUI({ openDrawer: true, awaitDrawer: false });
        } else if (window.KakaraCart?.openDrawer) {
          window.KakaraCart.openDrawer();
        }

        const elapsed = performance.now() - startedAt;
        if (elapsed < 720) {
          await new Promise((resolve) => setTimeout(resolve, 720 - elapsed));
        }
      } catch (error) {
        console.error('Error adding candle + hanger:', error);
        alert(window.theme?.strings?.cartError || 'There was an error updating your cart');
      } finally {
        btn.classList.remove('is-success');
        btn.textContent = originalText;
        btn.disabled = !this.currentVariant?.available;
      }
    },

    applyThumbnailImage(thumb) {
      if (!thumb) return;
      const mainImage = this.container.querySelector('[data-product-featured-image]');
      if (mainImage) {
        if (thumb.dataset.imageSrc) mainImage.src = thumb.dataset.imageSrc;
        if (thumb.dataset.imageSrcset) mainImage.srcset = thumb.dataset.imageSrcset;
        mainImage.sizes = mainImage.sizes || '(min-width: 1024px) 50vw, 100vw';
        if (thumb.dataset.imageWidth) mainImage.width = thumb.dataset.imageWidth;
        if (thumb.dataset.imageHeight) mainImage.height = thumb.dataset.imageHeight;
        if (thumb.dataset.imageAlt) mainImage.alt = thumb.dataset.imageAlt;
      }
      this.setActiveThumbnail(thumb);
    },

    setActiveThumbnail(activeThumbnail) {
      this.container.querySelectorAll('[data-product-thumbnail]').forEach((thumb) => {
        const isActive = thumb === activeThumbnail;
        thumb.classList.toggle('product-template__thumbnail--active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    },

    syncOptionButtons(index, value) {
      this.container.querySelectorAll(`[data-option-value][data-option-index="${index}"]`).forEach((btn) => {
        const selected = btn.dataset.optionValue === value;
        btn.classList.toggle('product-template__option-button--selected', selected);
        btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
    },

    updateOption(value, group) {
      group.querySelectorAll('[data-option-value]').forEach((btn) => {
        const selected = btn.dataset.optionValue === value;
        btn.classList.toggle('product-template__option-button--selected', selected);
        btn.setAttribute('aria-checked', selected ? 'true' : 'false');
      });
      const index = group.querySelector('[data-option-index]')?.dataset.optionIndex;
      const select = this.container.querySelector(`[data-option-select][data-option-index="${index}"]`);
      if (select) select.value = value;
      this.updateVariantFromOptions();
    },

    updateVariantFromOptions() {
      const byIndex = [];

      this.container.querySelectorAll('[data-option-value].product-template__option-button--selected').forEach((btn) => {
        byIndex[Number(btn.dataset.optionIndex)] = btn.dataset.optionValue;
      });

      this.container.querySelectorAll('[data-option-select]').forEach((select) => {
        const idx = Number(select.dataset.optionIndex);
        if (!Number.isNaN(idx) && select.value) byIndex[idx] = select.value;
      });

      const options = byIndex.filter((value) => value != null);

      // Find matching variant
      const variant = this.productJson.variants.find(v => {
        return v.options.every((opt, index) => opt === options[index]);
      });

      if (variant) {
        this.currentVariant = variant;
        this.updateVariantDisplay();
      }
    },

    updateVariantDisplay() {
      const variant = this.currentVariant;

      // Update variant ID
      const variantInput = this.container.querySelector('[data-variant-id]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      // Update price
      const priceContainer = this.container.querySelector('.product-template__price');
      if (priceContainer) {
        let priceHtml = '';

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const savingsPercent = Math.round(
            ((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price
          );
          priceHtml = `
            <div class="price price--on-sale">
              <span class="price__regular">
                <span class="visually-hidden">${window.theme?.strings?.regularPrice || 'Regular price'}</span>
                <s class="price__compare">${this.formatMoney(variant.compare_at_price)}</s>
                <span class="price__current">${this.formatMoney(variant.price)}</span>
              </span>
              <span class="price__savings">Save ${savingsPercent}%</span>
            </div>
          `;
        } else {
          priceHtml = `
            <div class="price">
              <span class="price__regular">
                <span class="price__current">${this.formatMoney(variant.price)}</span>
              </span>
            </div>
          `;
        }

        priceContainer.innerHTML = priceHtml;
      }

      // Update add to cart buttons (main + sticky)
      const atcLabel = variant.available
        ? (window.theme?.strings?.addToCart || 'Add to cart')
        : (window.theme?.strings?.soldOut || 'Sold out');
      this.container.querySelectorAll('[data-add-to-cart], [data-sticky-atc-button]').forEach((btn) => {
        btn.disabled = !variant.available;
        btn.textContent = atcLabel;
      });

      const roadBtn = this.container.querySelector('[data-candle-road-atc]');
      if (roadBtn && !roadBtn.classList.contains('is-success')) {
        roadBtn.disabled = !variant.available;
      }

      const stickyPrice = this.container.querySelector('[data-sticky-atc-price]');
      if (stickyPrice) {
        stickyPrice.textContent = this.formatMoney(variant.price);
      }

      this.updateSpecs(variant);
      this.updatePackDetail(variant);
      this.updateSizeFit(variant);
      this.updateScentStory(variant);

      // Size clicks (Mini/Regular) swap to the variant packshot. First paint stays lifestyle.
      if (variant.featured_image?.id) {
        const matchingThumb = this.container.querySelector(
          `[data-product-thumbnail][data-image-id="${variant.featured_image.id}"]`
        );
        if (matchingThumb) {
          this.applyThumbnailImage(matchingThumb);
        }
      }

      // Update URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    },

    formatMoney(cents) {
      if (window.theme?.moneyFormat) {
        return window.theme.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
      }
      return '$' + (cents / 100).toFixed(2);
    },

    isCarProduct() {
      const type = String(this.productJson?.type || this.productJson?.product_type || '').toLowerCase();
      return type.includes('car');
    },

    updateSpecs(variant) {
      const el = this.container.querySelector('[data-product-specs]');
      if (!el) return;

      if (this.isCarProduct()) {
        el.textContent = el.dataset.specsCar || el.dataset.specsFallback || '';
        return;
      }

      const title = String(variant?.title || '').toLowerCase();
      let line = el.dataset.specsFallback || '';
      if (title.includes('mini')) line = el.dataset.specsMini || line;
      else if (title.includes('regular')) line = el.dataset.specsRegular || line;
      else if (title.includes('car')) line = el.dataset.specsCar || line;
      el.textContent = line;
    },

    updateSizeFit(variant) {
      const el = this.container.querySelector('[data-product-size-fit]');
      if (!el) return;

      if (this.isCarProduct()) {
        el.textContent = '';
        el.hidden = true;
        return;
      }

      const title = String(variant?.title || '').toLowerCase();
      let line = '';
      if (title.includes('mini')) line = el.dataset.sizeFitMini || '';
      else if (title.includes('regular')) line = el.dataset.sizeFitRegular || '';
      el.textContent = line;
      el.hidden = !line;
    },

    updatePackDetail(variant) {
      const el = this.container.querySelector('[data-product-pack-detail]');
      if (!el) return;

      if (this.isCarProduct()) {
        const line = el.dataset.packCar || '';
        el.textContent = line;
        el.hidden = !line;
        return;
      }

      const title = String(variant?.title || '').toLowerCase();
      let line = '';
      if (title.includes('mini')) line = el.dataset.packMini || '';
      else if (title.includes('regular')) line = el.dataset.packRegular || '';
      else if (title.includes('car')) line = el.dataset.packCar || '';

      el.textContent = line;
      el.hidden = !line;
    },

    updateScentStory(variant) {
      const storyEl = this.container.querySelector('[data-product-scent-story]');
      const jsonEl = this.container.querySelector('[data-scent-stories]');
      if (!storyEl || !jsonEl) return;

      let stories = {};
      try {
        stories = JSON.parse(jsonEl.textContent);
      } catch (error) {
        return;
      }

      const story = stories[String(variant?.id)] || '';
      storyEl.hidden = !story;
      storyEl.textContent = story;
    },

    initStickyAtc() {
      const bar = this.container.querySelector('[data-sticky-atc]');
      const buyBox = this.container.querySelector('.product-template__buy-buttons');
      if (!bar || !buyBox || !('IntersectionObserver' in window)) return;

      bar.hidden = false;

      const observer = new IntersectionObserver(
        ([entry]) => {
          bar.classList.toggle('is-visible', !entry.isIntersecting);
        },
        { threshold: 0, rootMargin: '0px 0px -8% 0px' }
      );
      observer.observe(buyBox);
    },

    loadRecommendations() {
      const section = document.querySelector('[data-product-recommendations]');
      const url = section?.dataset?.url;
      if (!section || !url) return;

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error('Recommendations request failed');
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incoming = doc.querySelector('[data-product-recommendations]');
          if (incoming && incoming.innerHTML.trim()) {
            section.innerHTML = incoming.innerHTML;
          }
        })
        .catch((error) => {
          console.warn('Product recommendations:', error);
        });
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    product.init();
  });
})();
